/**
 * OpenMeta — Analytics client léger (fire-and-forget, jamais bloquant).
 *
 * - session_id : UUID anonyme persisté en localStorage 'om_session_id'
 * - track(event, extra) : POST /api/analytics/event sans await (Promise catch silencieux)
 * - N'ENVOIE JAMAIS DE PII (email, IP) : l'appelant doit s'assurer que `extra` ne contient
 *   pas d'email ni de contenu utilisateur.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'om_session_id';

function uuid() {
  // Fallback si crypto.randomUUID pas dispo (Safari < 15.4)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (_) {}
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getSessionId() {
  if (typeof window === 'undefined') return 'server';
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = uuid();
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch (_) {
    return uuid();
  }
}

const analyticsClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  // On garde un timeout court : si le back tarde, on abandonne
  timeout: 4000,
});

const ALLOWED_EVENTS = new Set([
  'generator_view',
  'generate_started',
  'generate_success',
  'generate_failed',
  'unlock_shown',
  'unlock_submitted',
  'unlock_success',
  'waitlist_joined',
  'contact_submitted',
  'checkout_started',
  'prototype_generated',
  'prototype_viewed',
  'signup_completed',
  'app_created',
  'app_published',
]);

/**
 * Fire-and-forget : n'attend pas le résultat, ne remonte jamais d'erreur.
 * @param {string} event
 * @param {{ lang?: string, vertical?: string, concept_id?: string }} [extra]
 */
export function track(event, extra = {}) {
  if (!ALLOWED_EVENTS.has(event)) return;
  const body = {
    event,
    session_id: getSessionId(),
    lang: extra.lang || undefined,
    vertical: extra.vertical || undefined,
    concept_id: extra.concept_id || undefined,
  };
  // Volontairement pas de await
  analyticsClient.post('/analytics/event', body).catch(() => {});
}

export const ANALYTICS_STORAGE_KEY = STORAGE_KEY;
